import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../shared/apiClient';
import { resolveBillingPlansFromApi } from '../../shared/billing/defaultPublicPlans';
import { PlanCard, type BillingCycle, type PlanCardPlan } from '../../components/PlanCard/PlanCard';
import { RelixLogo } from '../../components/brand/RelixLogo';
import { loadTeamFromJira } from '../../shared/team/loadTeamFromJira';
import {
  isOrganizationOnboardingComplete,
  isSupportCustomerSession,
  markOnboardingCompleted,
  pickOrgForOnboardingCheck,
} from '../../shared/onboarding/onboardingState';
import {
  OnboardingProgressBar,
  type OnboardingProgressStepMeta,
} from '../../components/OnboardingProgressBar/OnboardingProgressBar';
import { JiraConnection } from '../../features/integrations/JiraConnection';
import { supabase } from 'lib/supabase';
import { getApiBaseUrl } from '../../shared/config';
import {
  LS_ORG_ID,
  LS_PRODUCT_ID,
  LS_ONBOARDING_ACTIVE_ORG_ID,
  LS_ONBOARDING_STEP_PREFIX,
} from '../../shared/config/storageKeys';
import './OnboardingPage.css';

/** Survives stale React state after refreshUser(); cleared with resetAllOnboarding prefix if we add it later */
const ONBOARDING_ACTIVE_ORG_KEY = LS_ONBOARDING_ACTIVE_ORG_ID;
const ONBOARDING_STEP_KEY_PREFIX = LS_ONBOARDING_STEP_PREFIX;

/**
 * Fallback: GET /api/auth/me with Authorization only (never use apiFetch — it adds X-Organization-Id
 * from localStorage and a stale UUID makes requireAuth return 403 before the route runs).
 * Prefer organizations from refreshUser() in AuthContext when possible.
 */
async function syncMemberOrgIdFromApi(): Promise<string | null> {
  const base = getApiBaseUrl();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  const res = await fetch(`${base}/api/auth/me`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) return null;
  const data = await res.json();
  const orgs = data.organizations as { id: string }[] | undefined;
  if (!Array.isArray(orgs) || orgs.length === 0) return null;
  const validIds = new Set(orgs.map((o) => o.id));

  const saved = localStorage.getItem(LS_ORG_ID);
  if (saved && !validIds.has(saved)) {
    try {
      localStorage.removeItem(LS_ORG_ID);
    } catch {
      // ignore
    }
  }
  try {
    const active = sessionStorage.getItem(ONBOARDING_ACTIVE_ORG_KEY);
    if (active && !validIds.has(active)) {
      sessionStorage.removeItem(ONBOARDING_ACTIVE_ORG_KEY);
    }
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(ONBOARDING_STEP_KEY_PREFIX)) {
        const id = k.slice(ONBOARDING_STEP_KEY_PREFIX.length);
        if (!validIds.has(id)) {
          sessionStorage.removeItem(k);
        }
      }
    }
  } catch {
    // ignore
  }

  const savedAfter = localStorage.getItem(LS_ORG_ID);
  const match = savedAfter && validIds.has(savedAfter) ? savedAfter : null;
  const id = match ?? orgs[0].id;
  try {
    localStorage.setItem(LS_ORG_ID, id);
    sessionStorage.setItem(ONBOARDING_ACTIVE_ORG_KEY, id);
  } catch {
    // ignore
  }
  return id;
}

const STEP_META: OnboardingProgressStepMeta[] = [
  { label: 'Workspace', subtitle: 'Name your organization' },
  { label: 'Plan', subtitle: 'Choose your plan and start your trial' },
  { label: 'Jira', subtitle: 'Connect Jira to sync worklogs and team' },
  { label: 'Team', subtitle: 'Import assignable users for capacity' },
];

/**
 * Visiting /onboarding when setup is already done: redirect to the app (see useEffect below).
 * Keeps a single obvious behavior for returning users and bookmarked URLs.
 */
export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, organizations, createOrganization, refreshUser, currentOrg, currentProduct } =
    useAuth();
  const stepRestoreRef = useRef(false);
  const [step, setStep] = useState(0);
  const [compactProgress, setCompactProgress] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  const [workspaceName, setWorkspaceName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [plans, setPlans] = useState<PlanCardPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedSlug, setSelectedSlug] = useState<string>('growth');
  const [trialSaving, setTrialSaving] = useState(false);

  const [jiraSavedThisSession, setJiraSavedThisSession] = useState(false);

  const [teamLoading, setTeamLoading] = useState(false);
  const [teamMessage, setTeamMessage] = useState<string | null>(null);
  const [teamSummary, setTeamSummary] = useState<{
    added: number;
    updated: number;
    skipped: number;
  } | null>(null);

  const [teamImportOpen, setTeamImportOpen] = useState(false);
  const [teamImportLogs, setTeamImportLogs] = useState<string[]>([]);
  const [teamImportPercent, setTeamImportPercent] = useState(0);
  const [teamImportLabel, setTeamImportLabel] = useState('');
  const teamImportLogEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setCompactProgress(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** Support impersonation uses customer org id in LS; skip owner onboarding wizard entirely. */
  useEffect(() => {
    if (isSupportCustomerSession()) {
      navigate('/analytics', { replace: true });
    }
  }, [navigate]);

  /** Already completed onboarding: always leave this route for /analytics. */
  useEffect(() => {
    if (!user || !organizations.length) return;
    const org = pickOrgForOnboardingCheck(organizations, localStorage.getItem(LS_ORG_ID));
    if (org && isOrganizationOnboardingComplete(org)) {
      navigate('/analytics', { replace: true });
    }
  }, [user, organizations, navigate]);

  /**
   * Non-owner on an unfinished onboarding: this page is Owner-only (the final
   * PATCH /api/organizations/:orgId/onboarding is gated by
   * `organization.complete_onboarding`). Admins and members land on /analytics
   * and wait for the owner to finish setup.
   *
   * `pickOrgForOnboardingCheck` narrows its input's type and doesn't surface
   * `role`; look it up on the original `organizations` list instead.
   */
  useEffect(() => {
    if (!user || !organizations.length) return;
    const picked = pickOrgForOnboardingCheck(organizations, localStorage.getItem(LS_ORG_ID));
    if (!picked) return;
    if (isOrganizationOnboardingComplete(picked)) return;
    const fullOrg = organizations.find((o) => o.id === picked.id);
    if (fullOrg && fullOrg.role && fullOrg.role !== 'owner') {
      navigate('/analytics', { replace: true });
    }
  }, [user, organizations, navigate]);

  /**
   * One-time restore when org list loads: session step, else start at Plan (step 1) if org exists.
   * Never restore "0" with an existing org (avoids persisting step 0 briefly after create).
   */
  useEffect(() => {
    if (stepRestoreRef.current || !organizations.length) return;
    const org = pickOrgForOnboardingCheck(organizations, localStorage.getItem(LS_ORG_ID));
    if (!org || isOrganizationOnboardingComplete(org)) return;

    stepRestoreRef.current = true;
    try {
      const raw = sessionStorage.getItem(`${LS_ONBOARDING_STEP_PREFIX}${org.id}`);
      if (raw != null) {
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 0 && n <= 3) {
          const effective = organizations.length > 0 && n === 0 ? 1 : n;
          setStep(effective);
          return;
        }
      }
    } catch {
      // ignore
    }
    setStep(1);
  }, [organizations]);

  useEffect(() => {
    const orgId = currentOrg?.id || organizations[0]?.id;
    if (!orgId || step < 0) return;
    if (organizations.length > 0 && step === 0) return;
    try {
      sessionStorage.setItem(`${LS_ONBOARDING_STEP_PREFIX}${orgId}`, String(step));
    } catch {
      // ignore
    }
  }, [step, currentOrg?.id, organizations]);

  /** Backup org id for billing API when localStorage was cleared by a failed / partial auth/me sync */
  useEffect(() => {
    const id = currentOrg?.id || organizations[0]?.id;
    if (!id) return;
    try {
      sessionStorage.setItem(ONBOARDING_ACTIVE_ORG_KEY, id);
    } catch {
      // ignore
    }
  }, [currentOrg?.id, organizations]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/billing/plans');
        if (!res.ok) throw new Error('Failed to load plans');
        const data = await res.json();
        if (!cancelled) setPlans(resolveBillingPlansFromApi(data.plans));
      } catch {
        if (!cancelled) setPlans(resolveBillingPlansFromApi(null));
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onboardingApiFetch = useCallback(
    (path: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      const orgId = currentOrg?.id || localStorage.getItem(LS_ORG_ID);
      if (orgId && !headers.has('X-Organization-Id')) {
        headers.set('X-Organization-Id', orgId);
      }
      const productId = currentProduct?.id || localStorage.getItem(LS_PRODUCT_ID);
      if (productId && !headers.has('X-Product-Id')) {
        headers.set('X-Product-Id', productId);
      }
      return apiFetch(path, { ...init, headers });
    },
    [currentOrg?.id, currentProduct?.id]
  );

  const completeOnboardingAndNavigate = useCallback(
    async (target: '/analytics' | '/releases') => {
      const orgs = await refreshUser();
      const saved = localStorage.getItem(LS_ORG_ID);
      let orgId =
        (saved && orgs.some((o) => o.id === saved) ? saved : orgs[0]?.id) ??
        (await syncMemberOrgIdFromApi());
      if (orgId) {
        markOnboardingCompleted(orgId);
        try {
          await apiFetch(`/api/organizations/${orgId}/onboarding`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-Organization-Id': orgId,
            },
            body: JSON.stringify({ completed: true }),
          });
        } catch {
          // local flag still set; user can retry from settings if API failed
        }
      }
      await refreshUser();
      navigate(target, { replace: true });
    },
    [refreshUser, navigate]
  );

  const handleWorkspaceContinue = useCallback(async () => {
    const name = workspaceName.trim();
    if (!name) {
      setCreateError('Enter a workspace name');
      return;
    }
    setCreating(true);
    setCreateError(null);
    const { error } = await createOrganization(name);
    setCreating(false);
    if (error) {
      setCreateError(error);
      return;
    }
    await refreshUser();
    setStep(1);
    try {
      const oid = localStorage.getItem(LS_ORG_ID);
      if (oid) sessionStorage.setItem(ONBOARDING_ACTIVE_ORG_KEY, oid);
    } catch {
      // ignore
    }
  }, [workspaceName, createOrganization, refreshUser]);

  const handleTrialContinue = useCallback(async () => {
    setTrialSaving(true);
    setCreateError(null);
    try {
      /**
       * Use organizations returned by refreshUser() (GET /api/auth/me with Authorization only).
       * syncMemberOrgIdFromApi() uses the same; do not rely on apiFetch('/api/auth/me') here — apiFetch
       * always attaches X-Organization-Id from localStorage, and a stale UUID makes requireAuth return 403.
       */
      const orgs = await refreshUser();
      const saved = localStorage.getItem(LS_ORG_ID);
      let orgId =
        (saved && orgs.some((o) => o.id === saved) ? saved : orgs[0]?.id) ??
        (await syncMemberOrgIdFromApi());
      if (!orgId) {
        setCreateError(
          'Workspace is not ready yet. Please refresh the page, or go back and create your workspace again.'
        );
        return;
      }
      try {
        localStorage.setItem(LS_ORG_ID, orgId);
        sessionStorage.setItem(ONBOARDING_ACTIVE_ORG_KEY, orgId);
      } catch {
        // ignore
      }

      const advanceToJira = async () => {
        await refreshUser();
        setJiraSavedThisSession(false);
        setStep(2);
      };

      const res = await apiFetch('/api/billing/select-trial-plan', {
        method: 'POST',
        headers: {
          'X-Organization-Id': orgId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planSlug: selectedSlug }),
      });

      if (res.ok) {
        await advanceToJira();
        return;
      }

      /** Org often already has a trial row from signup; POST may 403 for non-owners or 400 if plan slug mismatches DB. */
      const subRes = await apiFetch('/api/billing/subscription', {
        headers: { 'X-Organization-Id': orgId },
      });
      if (subRes.ok) {
        const data = (await subRes.json().catch(() => ({}))) as {
          subscription?: { status?: string };
        };
        const st = data.subscription?.status;
        if (st === 'trialing' || st === 'active') {
          await advanceToJira();
          return;
        }
      }

      const err = await res.json().catch(() => ({}));
      const message = (err as { error?: string }).error || 'Could not save plan';
      setCreateError(message);
    } catch {
      setCreateError('Network error');
    } finally {
      setTrialSaving(false);
    }
  }, [selectedSlug, refreshUser]);

  useEffect(() => {
    if (!teamImportOpen || teamImportLogs.length === 0) return;
    teamImportLogEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [teamImportOpen, teamImportLogs]);

  useEffect(() => {
    if (!teamImportOpen || teamLoading) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTeamImportOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [teamImportOpen, teamLoading]);

  const handleTeamLoadFromJira = useCallback(async () => {
    setTeamMessage(null);
    setTeamSummary(null);
    setTeamImportOpen(true);
    setTeamImportLogs([]);
    setTeamImportPercent(0);
    setTeamImportLabel('');
    setTeamLoading(true);
    try {
      let productId = currentProduct?.id?.trim() ? String(currentProduct.id).trim() : null;
      if (!productId && typeof window !== 'undefined') {
        const fromLs = window.localStorage.getItem(LS_PRODUCT_ID);
        if (fromLs?.trim()) productId = fromLs.trim();
      }
      if (!productId) {
        await refreshUser();
        productId = currentProduct?.id?.trim() ? String(currentProduct.id).trim() : productId;
      }
      if (!productId || !productId.trim()) {
        setTeamMessage(
          'No product selected. Finish the integration step first or pick your product header, then try again.'
        );
        return;
      }
      const result = await loadTeamFromJira(onboardingApiFetch, {
        productId,
        onProgress: (ev) => {
          if (ev.type === 'log') {
            setTeamImportLogs((prev) => [...prev.slice(-400), ev.message]);
          } else {
            setTeamImportPercent(ev.percent);
            setTeamImportLabel(ev.label ?? '');
          }
        },
      });
      if (!result.ok) {
        setTeamMessage(result.error);
        return;
      }
      setTeamSummary({ added: result.added, updated: result.updated, skipped: result.skipped });
      setTeamMessage(
        `Imported ${result.members.length} team member(s). You can adjust roles in Settings.`
      );
    } catch (e) {
      setTeamMessage(e instanceof Error ? e.message : 'Failed to import team');
    } finally {
      setTeamLoading(false);
    }
  }, [onboardingApiFetch, currentProduct?.id, refreshUser]);

  const goBack = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const handleProgressStepClick = useCallback((i: number) => {
    setStep((s) => (i < s ? i : s));
  }, []);

  if (!user) {
    return null;
  }

  const teamImportModal =
    teamImportOpen &&
    createPortal(
      <div
        className="team-import-modal-root"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-import-title"
        aria-busy={teamLoading}
      >
        <div
          className="team-import-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!teamLoading) setTeamImportOpen(false);
          }}
        />
        <div className="team-import-modal-panel">
          <h2 id="team-import-title" className="team-import-modal-title">
            Importing team from Jira
          </h2>
          <div className="team-import-modal-progress" aria-hidden>
            <div
              className="team-import-modal-progress-fill"
              style={{ width: `${teamImportPercent}%` }}
            />
          </div>
          <p className="team-import-modal-status">
            {teamImportLabel || (teamLoading ? 'Working…' : 'Finished')}
          </p>
          <div className="team-import-modal-log" tabIndex={0}>
            {teamImportLogs.length === 0 && teamLoading ? (
              <p className="team-import-modal-log-empty">Starting…</p>
            ) : (
              teamImportLogs.map((line, i) => (
                <div key={i} className="team-import-modal-log-line">
                  {line}
                </div>
              ))
            )}
            <div ref={teamImportLogEndRef} />
          </div>
          <div className="team-import-modal-footer">
            <button
              type="button"
              className="onboarding-primary team-import-modal-close"
              disabled={teamLoading}
              onClick={() => setTeamImportOpen(false)}
            >
              {teamLoading ? (
                <>
                  <Loader2 className="onboarding-spin" aria-hidden />
                  In progress…
                </>
              ) : (
                'Close'
              )}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="onboarding-page">
      {teamImportModal}
      <div className="onboarding-inner animate-fade-in">
        <OnboardingProgressBar
          currentStep={step}
          steps={STEP_META}
          compact={compactProgress}
          onStepClick={handleProgressStepClick}
        />

        <div className="onboarding-brand">
          <RelixLogo size={64} showWordmark={false} markClassName="onboarding-brand-mark" />
        </div>

        {step === 0 && (
          <div className="onboarding-panel animate-slide-up">
            <h1 className="onboarding-title">Name your workspace</h1>
            <p className="onboarding-sub">
              This is how your organization appears in Relix. You can change it later.
            </p>
            <label className="onboarding-label" htmlFor="ws-name">
              Workspace name
            </label>
            <input
              id="ws-name"
              className="onboarding-input"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="e.g. Acme Engineering"
              autoComplete="organization"
            />
            {createError ? (
              <p className="onboarding-error" role="alert">
                {createError}
              </p>
            ) : null}
            <div className="onboarding-footer-actions">
              <button
                type="button"
                className="onboarding-primary"
                disabled={creating}
                onClick={() => void handleWorkspaceContinue()}
              >
                {creating ? <Loader2 className="onboarding-spin" /> : null}
                {creating ? 'Creating…' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="onboarding-panel onboarding-panel--plan animate-slide-up">
            <h1 className="onboarding-title">Choose your plan</h1>
            <p className="onboarding-sub">
              Start with a 14-day free trial. No credit card required.
            </p>
            <div className="onboarding-billing-toggle">
              <button
                type="button"
                className={billingCycle === 'monthly' ? 'active' : ''}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={billingCycle === 'annual' ? 'active' : ''}
                onClick={() => setBillingCycle('annual')}
              >
                Annual <span className="onboarding-save-badge">Save 17%</span>
              </button>
            </div>
            {plansLoading ? (
              <div className="onboarding-loading">
                <Loader2 className="onboarding-spin" />
              </div>
            ) : (
              <div className="onboarding-plans">
                {plans.map((p) => (
                  <div
                    key={p.id}
                    className={`onboarding-plan-wrap ${selectedSlug === p.slug ? 'selected' : ''}`}
                    onClick={() => setSelectedSlug(p.slug)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelectedSlug(p.slug);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <PlanCard
                      plan={p}
                      billingCycle={billingCycle}
                      featured={p.slug === 'scale'}
                      badge={p.slug === 'scale' ? 'Most popular' : undefined}
                      isEnterprise={p.slug === 'enterprise'}
                      ctaLabel={p.slug === 'enterprise' ? 'Contact sales' : 'Select'}
                      onCta={() => setSelectedSlug(p.slug)}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="onboarding-plan-sticky-footer">
              {createError ? (
                <p className="onboarding-error onboarding-error--plan" role="alert">
                  {createError}
                </p>
              ) : null}
              <div className="onboarding-footer-actions onboarding-footer-actions--split">
                <button type="button" className="onboarding-secondary" onClick={goBack}>
                  Back
                </button>
                <button
                  type="button"
                  className="onboarding-primary"
                  disabled={trialSaving || selectedSlug === 'enterprise'}
                  onClick={() => void handleTrialContinue()}
                >
                  {trialSaving ? <Loader2 className="onboarding-spin" /> : null}
                  {trialSaving ? 'Saving…' : 'Start 14-day free trial'}
                </button>
              </div>
            </div>
            {selectedSlug === 'enterprise' ? (
              <p className="onboarding-hint">
                For Enterprise, contact our team — we&apos;ll set up your workspace.
              </p>
            ) : null}
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-panel animate-slide-up onboarding-panel--jira">
            <h1 className="onboarding-title">Connect Jira</h1>
            <p className="onboarding-sub">
              Enter your Jira Cloud domain, account email, project key, and API token. We use this
              to sync worklogs and load your team. You can skip and configure later in Settings.
            </p>
            <JiraConnection
              variant="compact"
              hideHelp
              onCredentialsSaved={() => setJiraSavedThisSession(true)}
            />
            <div className="onboarding-footer-actions onboarding-footer-actions--split onboarding-footer-actions--wrap">
              <button type="button" className="onboarding-secondary" onClick={goBack}>
                Back
              </button>
              <button type="button" className="onboarding-secondary" onClick={() => setStep(3)}>
                Skip for now
              </button>
              <button
                type="button"
                className="onboarding-primary"
                disabled={!jiraSavedThisSession}
                onClick={() => setStep(3)}
                title={
                  !jiraSavedThisSession ? 'Save Jira credentials above to continue' : undefined
                }
              >
                Continue to team
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-panel animate-slide-up onboarding-panel--team">
            <h1 className="onboarding-title">
              <Users size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Import your team
            </h1>
            <p className="onboarding-sub">
              Load assignable users from Jira into Relix so we can calculate capacity. You can
              fine-tune roles later in Settings → Team Management.
            </p>
            {teamMessage ? (
              <p
                className={
                  teamMessage.startsWith('Imported') ? 'onboarding-hint' : 'onboarding-error'
                }
                role={teamMessage.startsWith('Imported') ? undefined : 'alert'}
              >
                {teamMessage}
              </p>
            ) : null}
            {teamSummary ? (
              <p className="onboarding-hint" style={{ fontSize: '13px' }}>
                Added {teamSummary.added}, updated {teamSummary.updated}, skipped{' '}
                {teamSummary.skipped}.
              </p>
            ) : null}
            <div className="onboarding-actions onboarding-actions--stack">
              <button
                type="button"
                className="onboarding-secondary"
                disabled={teamLoading}
                onClick={() => void handleTeamLoadFromJira()}
              >
                {teamLoading ? <Loader2 className="onboarding-spin" /> : null}
                {teamLoading ? 'Loading…' : 'Load from Jira'}
              </button>
            </div>
            <div className="onboarding-footer-actions onboarding-footer-actions--split onboarding-footer-actions--wrap">
              <button type="button" className="onboarding-secondary" onClick={goBack}>
                Back
              </button>
              <button
                type="button"
                className="onboarding-secondary"
                onClick={() => void completeOnboardingAndNavigate('/releases')}
              >
                Open Releases
              </button>
              <button
                type="button"
                className="onboarding-primary"
                onClick={() => void completeOnboardingAndNavigate('/analytics')}
              >
                Finish setup
              </button>
            </div>
            <button
              type="button"
              className="onboarding-link-btn"
              onClick={() => void completeOnboardingAndNavigate('/analytics')}
            >
              Skip — go to app
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
